import axios from 'axios';
import { ENV_VARIABLES } from '@/consts/common';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import { PKCE, State } from '@/utils/helpers/authHelpers';
import { normalizeLanguageCode } from '@/utils/helpers/commonHelpers';

const {
  VITE_FRONTENDWEB_BASEURL,
  VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID,
  VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL,
} = ENV_VARIABLES;

const CALLBACK_REDIRECT_URI = `${VITE_FRONTENDWEB_BASEURL}/auth/callback`;

interface IRefreshTokens {
  access_token: string;
  id_token: string;
  token_type: string;
  expires_in: number;
}
interface IExchangeCodeForTokens extends IRefreshTokens {
  refresh_token: string;
}

/**
 *
 * @param authType login or signup
 * redirects to the cognito Managed Login for login/signup
 */
async function initiateAuth(authType: 'login' | 'signup', appLang: string) {
  const responseType = 'code';
  const scope = 'openid+email';
  const state = btoa(State.generateStateParam());
  const codeChallengeMethod = 'S256';
  const codeVerifier = PKCE.generateVerifier();
  const codeChallenge = await PKCE.generateChallenge(codeVerifier);
  Storage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  const lang = normalizeLanguageCode(appLang);

  window.location.href = `${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL}/${authType}?response_type=${responseType}&client_id=${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID}&redirect_uri=${CALLBACK_REDIRECT_URI}&state=${state}&scope=${scope}&code_challenge=${codeChallenge}&code_challenge_method=${codeChallengeMethod}&lang=${lang}`;
}

/**
 *
 * @param code Authorization code returned from Cognito Managed Login UI is exchanged for tokens
 */
async function exchangeCodeForTokens(
  code: string,
): Promise<IExchangeCodeForTokens> {
  try {
    const url = `${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL}/oauth2/token`;
    const body = new URLSearchParams({
      redirect_uri: CALLBACK_REDIRECT_URI,
      client_id: VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID,
      code,
      grant_type: 'authorization_code',
      code_verifier: Storage.getItem(STORAGE_KEYS.CODE_VERIFIER) || '',
    });

    const response = await axios(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: body,
    });

    Storage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

    return response.data;
  } catch (error) {
    console.error(error);
    Storage.clearAllExceptIntl();
    throw new Error('Something went wrong. Try logging in again!');
  }
}

async function refreshTokens(refreshToken: string): Promise<IRefreshTokens | null> {
  try {
    const url = `${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
      client_id: VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID,
    });

    const response = await axios(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: body,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function revokeToken(refreshToken: string) {
  try {
    const url = `${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL}/oauth2/revoke`;
    const body = new URLSearchParams({
      token: refreshToken,
      client_id: VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID,
    });

    await axios(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      data: body,
    });
  } catch (error) {
    console.error(error);
  }
}

/**
 *
 * @param refreshToken refresh token to be revoked
 * removkes the token, clears the storage and
 * redirects to the logout endpoint to logout from Cognito
 */
async function globalLogOut(isHardLogout:boolean = true) {
  const refreshToken = Storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
  if (refreshToken) {
    await revokeToken(refreshToken);
  }
  // manually clearing everything because intl is also stored in localstorage
  Storage.clearAllExceptIntl();

  if (isHardLogout) {
    window.location.href = `${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_URL}/logout?client_id=${VITE_AWS_HOLOXR_FRONTENDWEB_USERPOOLCLIENT_ID}&logout_uri=${VITE_FRONTENDWEB_BASEURL}`;
  }
}

export {
  State, PKCE, initiateAuth, exchangeCodeForTokens, refreshTokens, globalLogOut,
};
